import { Button, Group } from '@mantine/core'
import { get, isEmpty } from 'lodash'
import React, { ReactElement, useMemo, useState } from 'react'

import CopyClipboardTrigger from '../../../../../../1stlog-ui/src/components/CopyClipboardTrigger'
import {
  useField,
  useFieldContext,
} from '../../../../../../1stlog-ui/src/components/Field'
import { useFieldArray } from '../../../../../../1stlog-ui/src/components/FieldArray'
import Icon from '../../../../../../1stlog-ui/src/components/Icon'
import InlineEditorValidation from '../../../../../../1stlog-ui/src/components/InlineEditor/InlineEditorValidation'
import Overlay from '../../../../../../1stlog-ui/src/components/Overlay'
import { useProductSettings } from '../../../Products'
import ColiServices from '../ColiServices'
import Barcode from './Barcode'
import { ColiLimitsValidation } from './ColiLimitsValidation'
import Content from './Content'
import Girth from './Girth'
import Measurements from './Measurements'
import Packaging from './Packaging'
import Reference from './Reference'
import Volume from './Volume'
import VolumeWeight from './VolumeWeight'
import Weight from './Weight'

// const Wrapper = styled.div``

// const ColiHeader = styled.div`
//   display: flex;
//   flex-wrap: wrap;
//   margin: -5px;
//   justify-content: center;
//   align-items: center;

//   & > * {
//     margin: 5px;
//   }
// `

// const ItemRow = styled.div`
//   display: flex;
//   flex-wrap: wrap;
//   margin: -10px -10px;

//   & > * {
//     margin: 0px 10px;
//   }
// `

type TColiProps = {
  index: number
  headerElement?: ReactElement
  isStorno?: boolean
  showBarcode?: boolean
  readonly?: boolean
  showInlineValidation?: boolean
  onDuplicate?: (index: number) => void
  onRemove?: (index: number) => void
  showColiCount?: boolean
  barcodePrefixElement?: ReactElement
}

const Coli = (props: TColiProps): ReactElement => {
  const {
    index = 0,
    headerElement = null,
    showInlineValidation,
    isStorno,
    showBarcode,
    readonly,
    onDuplicate,
    onRemove,
    showColiCount,
    barcodePrefixElement,
  } = props

  const isNew = useField('isNew')
  const barcode = useField('barcode1')
  const barcode2 = useField('barcode2')
  const barcode3 = useField('barcode3')

  const { maxColis } = useProductSettings()

  const { prefix: coliFieldName = '' } = useFieldContext()

  const barcodeElement = (
    <span>
      {barcodePrefixElement}
      {barcode} <CopyClipboardTrigger label="Barcode" content={barcode} />
    </span>
  )

  const [isHovered, setIsHovered] = useState(false)

  const onMouseEnter = (): void => setIsHovered(true)

  const onMouseLeave = (): void => setIsHovered(false)

  const duplicate = (): void => {
    if (onDuplicate) {
      onDuplicate(index)
    }
  }

  const remove = (): void => {
    if (onRemove) {
      onRemove(index)
    }
  }

  const { fields } = useFieldArray('colis', { ignorePrefix: true })
  const coliCount = useMemo(() => get(fields, 'length', 0), [fields])

  const isColiLimitReached = maxColis && maxColis > 0 && coliCount >= maxColis

  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      id={`coli_${index}`}
    >
      <div className="d-flex" style={{ position: 'relative' }}>
        {isHovered && (
          <div style={{ position: 'absolute', top: '0px', right: '0px' }}>
            <Group spacing="xs">
              {index > 0 && onRemove && (
                <Button size="xs" variant="subtle" color="red" onClick={remove}>
                  <Icon icon="far fa-trash" className="mr-2" />
                  {i18n.t('remove_coli_btn', 'Löschen')}
                </Button>
              )}
              {onDuplicate && !readonly && !isColiLimitReached && (
                <Button size="xs" variant="subtle" onClick={duplicate}>
                  <Icon icon="far fa-copy" className="mr-2" />
                  {i18n.t('duplicate_coli_btn', 'Duplizieren')}
                </Button>
              )}
            </Group>
          </div>
        )}
        {showBarcode ? (
          <h4 className="mr-2 mt-0 d-flex">
            <div className="mr-1">
              <Icon icon="fas fa-cube" />
            </div>
            {showColiCount ? (
              <div className="mr-2">
                Coli {index + 1}/{coliCount}
              </div>
            ) : (
              <div className="mr-2">Coli {index + 1}</div>
            )}
            {isNew ? (
              isEmpty(barcode) ? (
                <div>
                  <div className="text-muted">
                    {i18n.t('new_coli', 'Neues Coli')}
                  </div>
                  {/* <Tooltip withArrow label="Bitte Barcode angeben">
                    <Icon icon="fas fa-exclamation-triangle" warning /> Neues
                    Coli
                  </Tooltip> */}
                </div>
              ) : (
                <div>
                  {barcodeElement}
                  {/* {' '} */}
                  {/* <div className="text-muted">
                    ({i18n.t('new_coli', 'Neues Coli')})
                  </div> */}
                </div>
              )
            ) : (
              <div>
                {barcodeElement}{' '}
                <h5 className="text-small text-muted d-inline mt-1">
                  {[barcode2, barcode3].filter((e) => e && e !== '').join(', ')}
                </h5>
              </div>
            )}
          </h4>
        ) : (
          <h4 className="mr-2 mt-0">
            <i className="fas fa-cube" /> Coli {index + 1}
            {/* {barcode ? ` (${barcode})` : ""} */}
          </h4>
        )}
        <div className="flex-fill d-flex justify-content-end">
          {headerElement}
        </div>
      </div>
      {showInlineValidation && readonly && (
        <InlineEditorValidation fieldPaths={[coliFieldName]} />
      )}
      {!showInlineValidation && !readonly && <ColiLimitsValidation />}
      <div className="mt-2" style={{ position: 'relative' }}>
        {isStorno && <Overlay danger>Wird storniert</Overlay>}
        <div className="mb-2">
          <Group spacing="xl" style={{ alignItems: 'start' }}>
            <Weight readonly={readonly} />
            <Measurements readonly={readonly} />
            <Volume readonly={readonly} />
            <VolumeWeight readonly={readonly} />
            <Girth readonly={readonly} />
            <Reference readonly={readonly} />
            <Packaging readonly={readonly} />
            <Content readonly={readonly} />
            <Barcode hideOnReadonly={showBarcode} readonly={readonly} />
          </Group>
        </div>
        <div className="">
          <ColiServices coliFieldName={coliFieldName} readonly={readonly} />
        </div>
      </div>
    </div>
  )
}

export default Coli
